/**
 * Iter329 · Claire Actions widget for the executive dashboard.
 * Shows 24h/7d/30d counters + top tools + success rate + link to /tutorials.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, TrendingUp, CheckCircle2, ArrowRight } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_LABELS = {
  open_tutorial:  "Tutoriais",
  open_dossier:   "Dossiês",
  save_prospect:  "Prospects salvos",
  create_task:    "Tarefas criadas",
  email_status:   "Diagnósticos e-mail",
};

export default function ClaireActionsWidget() {
  const { t } = useLang(); // eslint-disable-line no-unused-vars
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/claire/actions/stats`);
        if (alive) setStats(data);
      } catch (_e) { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!stats) return null;

  const c = stats.counters || {};
  const top = stats.top_tools || [];

  return (
    <Card data-testid="claire-actions-widget"
          className="border-violet-200 bg-gradient-to-br from-violet-50/50 to-white dark:from-violet-900/10 dark:to-slate-900/30">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-violet-600 text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                Ações da Claire
              </h3>
              <p className="text-[11px] text-slate-500">
                Execuções assistidas pela IA
              </p>
            </div>
          </div>
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-200 gap-1">
            <CheckCircle2 className="w-3 h-3" /> {stats.success_rate_30d || 0}%
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { key: "h24", label: "24h" },
            { key: "d7",  label: "7d" },
            { key: "d30", label: "30d" },
          ].map((w) => (
            <div key={w.key}
                 data-testid={`claire-actions-count-${w.key}`}
                 className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5 text-center">
              <div className="text-xl font-black text-slate-900 dark:text-white">
                {c[w.key] ?? 0}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                {w.label}
              </div>
            </div>
          ))}
        </div>

        {top.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" /> Ferramentas mais usadas (30d)
            </div>
            <ul className="space-y-1">
              {top.slice(0, 4).map((t) => (
                <li key={t.tool}
                    data-testid={`claire-actions-top-${t.tool}`}
                    className="flex items-center justify-between text-[12px]">
                  <span className="text-slate-700 dark:text-slate-300">
                    {TOOL_LABELS[t.tool] || t.tool}
                  </span>
                  <span className="font-mono font-bold text-slate-900 dark:text-white tabular-nums">
                    {t.count}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Link
          to="/tutorials"
          data-testid="claire-actions-widget-cta"
          className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-violet-700 hover:text-violet-900">
          Ver tutoriais que a Claire pode abrir <ArrowRight className="w-3 h-3" />
        </Link>
      </CardContent>
    </Card>
  );
}
